/**
 * Pending-operations notifications.
 *
 * Long-running fetches show a non-dismissing notification in the
 * bottom-right corner with a live elapsed-time counter and a Cancel
 * button that aborts the underlying request.
 *
 * Backed by `@mantine/notifications` — we just feed it
 * `autoClose: false` + `withCloseButton: false` and a custom React
 * message body that ticks its own elapsed counter.
 *
 * Operations that finish under `TOAST_THRESHOLD_MS` never actually
 * reach the screen, so quick fetches stay silent.
 *
 * Usage:
 *
 *   const data = await withPendingOp(
 *     "Loading control.BatchRunLog…",
 *     (signal) => api.get(url, { signal }),
 *   );
 */
import { useEffect, useState } from "react";
import { ActionIcon, Group, Text, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";

/** Toast appears after this many ms — keeps quick fetches silent. */
export const TOAST_THRESHOLD_MS = 500;

let counter = 0;
const nextId = () => `pending-op-${++counter}`;

/**
 * Live elapsed-time + Cancel button. Re-renders itself every 100ms
 * while mounted; Mantine doesn't re-render the message on its own so
 * the timer has to live inside the body.
 */
function PendingOpBody({
  controller,
  startedAt,
}: {
  controller: AbortController;
  startedAt: number;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 100);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Date.now() - startedAt;
  return (
    <Group gap="xs" justify="space-between" wrap="nowrap">
      <Text
        size="xs"
        c="dimmed"
        style={{ fontFamily: "var(--font-mono)" }}
      >
        {formatElapsed(elapsed)}
      </Text>
      <Tooltip label="Cancel">
        <ActionIcon
          variant="subtle"
          color="red"
          size="sm"
          onClick={() => controller.abort()}
          aria-label="Cancel operation"
        >
          <IconX size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

function formatElapsed(ms: number): string {
  if (ms < 1000) {
    // Round down to the nearest 100ms so it doesn't flicker every tick.
    const t = Math.floor(ms / 100) * 100;
    return `${(t / 1000).toFixed(1)}s`;
  }
  const seconds = ms / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

/**
 * Schedule a Mantine notification for a long-running operation.
 *
 * The notification only actually appears after `TOAST_THRESHOLD_MS`,
 * so callers don't need to debounce themselves. Returns a `hide()`
 * helper that clears the pending timer (if not yet shown) or hides
 * the notification (if already on screen) — call it from a `finally`.
 */
export function showPendingNotification(
  label: string,
  controller: AbortController,
): { hide: () => void } {
  const id = nextId();
  const startedAt = Date.now();
  let shown = false;

  const timer = window.setTimeout(() => {
    shown = true;
    notifications.show({
      id,
      title: label,
      message: <PendingOpBody controller={controller} startedAt={startedAt} />,
      loading: true,
      autoClose: false,
      withCloseButton: false,
      withBorder: true,
      color: "blue",
    });
  }, TOAST_THRESHOLD_MS);

  return {
    hide: () => {
      window.clearTimeout(timer);
      if (shown) notifications.hide(id);
    },
  };
}

/**
 * Run an async operation while the pending-ops notification is on
 * screen. Provides an AbortSignal that the caller threads into
 * fetch/axios/etc; cancelling from the toast aborts the controller
 * and the fetch rejects with an AbortError.
 */
export async function withPendingOp<T>(
  label: string,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const handle = showPendingNotification(label, controller);
  try {
    return await run(controller.signal);
  } finally {
    handle.hide();
  }
}
